import axios from "axios";

const nativeConsole = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

const LEVELS = {
  DEBUG: 10,
  INFO: 20,
  WARNING: 30,
  ERROR: 40,
};

const API_BASE_URL = process.env.VUE_APP_API_URL || "";
const CLIENT_LOG_ENDPOINT = API_BASE_URL ? `${API_BASE_URL}/client-logs` : "";
const DEFAULT_LEVEL = process.env.NODE_ENV === "development" ? "DEBUG" : "INFO";

let activeLevel = DEFAULT_LEVEL;
let consoleOverridden = false;

function currentLevelValue() {
  return LEVELS[activeLevel] || LEVELS.INFO;
}

function normalizeLevel(level) {
  const upperLevel = `${level || "INFO"}`.toUpperCase();
  if (upperLevel === "WARN") return "WARNING";
  return LEVELS[upperLevel] ? upperLevel : "INFO";
}

function shouldLog(level) {
  return LEVELS[level] >= currentLevelValue();
}

function buildMessage(message, context) {
  if (!context || Object.keys(context).length === 0) {
    return message;
  }

  return `${message} | ${Object.entries(context)
    .map(([key, value]) => `${key}=${formatValue(value)}`)
    .join(" ")}`;
}

function formatValue(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[object]";
    }
  }
  return `${value}`;
}

function print(level, message, context = {}) {
  if (!shouldLog(level)) return;

  const prefix = `[${new Date().toISOString()}] [${level}] [frontend]`;
  const line = `${prefix} ${buildMessage(message, context)}`;

  if (level === "ERROR") nativeConsole.error(line);
  else if (level === "WARNING") nativeConsole.warn(line);
  else if (level === "DEBUG") nativeConsole.debug(line);
  else nativeConsole.info(line);
}

function persist(level, message, context = {}) {
  if (!CLIENT_LOG_ENDPOINT || level === "DEBUG") return;

  const payload = {
    level,
    message: `${message}`.slice(0, 500),
    source: "frontend",
    url: window.location?.href || null,
    status_code: context.status_code || null,
    context,
  };

  const body = JSON.stringify(payload);

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(CLIENT_LOG_ENDPOINT, blob);
      return;
    }
  } catch {}

  const headers = {
    "Content-Type": "application/json",
  };
  const authHeader = getAuthHeader();
  if (authHeader) {
    headers.Authorization = authHeader;
  }

  fetch(CLIENT_LOG_ENDPOINT, {
    method: "POST",
    headers,
    body,
    keepalive: true,
  }).catch(() => {});
}

function getAuthHeader() {
  const token = localStorage.getItem("token");
  return token ? `bearer ${token}` : "";
}

function log(level, message, context = {}) {
  const normalizedLevel = normalizeLevel(level);
  print(normalizedLevel, message, context);
  persist(normalizedLevel, message, context);
}

export function setLoggerLevel(level) {
  activeLevel = normalizeLevel(level);
}

export function syncAxiosAuthToken(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function installAxiosLogging(client) {
  if (!client || client.__loggerInstalled) return client;

  client.interceptors.request.use((config) => {
    config.metadata = { startedAt: Date.now() };
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const duration = Date.now() - (response.config.metadata?.startedAt || Date.now());
      log("INFO", "HTTP OK", {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status_code: response.status,
        duration_ms: duration,
      });
      return response;
    },
    (error) => {
      const statusCode = error?.response?.status || 0;
      const duration = Date.now() - (error?.config?.metadata?.startedAt || Date.now());
      log(statusCode >= 500 ? "ERROR" : "WARNING", "HTTP ERROR", {
        method: error?.config?.method?.toUpperCase(),
        url: error?.config?.url,
        status_code: statusCode,
        duration_ms: duration,
      });
      return Promise.reject(error);
    }
  );

  client.__loggerInstalled = true;
  return client;
}

export function createHttpClient(token = null) {
  const instance = axios.create();
  if (token) {
    instance.defaults.headers.common.Authorization = `bearer ${token}`;
  }
  installAxiosLogging(instance);
  return instance;
}

export function installGlobalErrorLogging() {
  window.addEventListener("error", (event) => {
    log("ERROR", "Unhandled error", {
      message: event.message,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    log("ERROR", "Unhandled promise rejection", {
      reason: formatValue(event.reason),
    });
  });
}

export function overrideConsole() {
  if (consoleOverridden) return;
  consoleOverridden = true;

  console.log = (...args) => {
    nativeConsole.log(...args);
    log("INFO", "console.log", { args: args.map(formatValue) });
  };
  console.info = (...args) => {
    nativeConsole.info(...args);
    log("INFO", "console.info", { args: args.map(formatValue) });
  };
  console.warn = (...args) => {
    nativeConsole.warn(...args);
    log("WARNING", "console.warn", { args: args.map(formatValue) });
  };
  console.error = (...args) => {
    nativeConsole.error(...args);
    log("ERROR", "console.error", { args: args.map(formatValue) });
  };
  console.debug = (...args) => {
    nativeConsole.debug(...args);
    log("DEBUG", "console.debug", { args: args.map(formatValue) });
  };
}

export const frontendLogger = {
  debug(message, context) {
    log("DEBUG", message, context);
  },
  info(message, context) {
    log("INFO", message, context);
  },
  warn(message, context) {
    log("WARNING", message, context);
  },
  error(message, context) {
    log("ERROR", message, context);
  },
};
